import React, { useEffect, useState } from "react";
import { CONSTANTS } from "@util/utils";
import dynamic from "next/dynamic";
import Theme from "/components/Theme";
import HeroWithLayeredImages from "../../components/organisms/common/HeroWithLayeredImages";
import DoubleCTA from "../../components/organisms/common/DoubleCTA";
import SignUpCTA from "../../components/organisms/common/SignupCTA";
import PagesList from "../../components/organisms/guides/PagesList";
import SubNav from "../../components/organisms/guides/SubNav";
import EmptyState from "../../components/organisms/guides/EmptyState";
import { ResultsContainer } from "../../components/organisms/common/CalendarCard";
import SubNavWithPagesHOC from "../../components/organisms/common/SubNavWithPagesHOC";
import { colors } from "@styles/cssinjs/helpers/variables";
import CarouselFullWidth from "../../components/organisms/common/CarouselFullWidth";
import HubCardsWithScroll from "../../components/organisms/common/HubCardsWithScroll";
import {
  font,
  lineHeight,
  viewport,
} from "../../styles/cssinjs/helpers/variables";
const BlogCardsSection = dynamic(() =>
  import("../../components/organisms/guides/BlogCardsSection")
);
const AppStorage = dynamic(() => import("../../components/AppStorage"));

export async function getStaticProps(ctx) {
  const { locale } = ctx;
  let allData = {};

  // Fetch
  const headerResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/common/header.json`
  );
  const footerResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/common/footer.json`
  );
  const marketingResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/guides/root/marketing.json`
  );
  const contentResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/guides/root/content.json`
  );
  const pagesResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/guides/root/pages.json`
  );
  // Catch Error
  if (marketingResponse.status === 404 || contentResponse.status === 404) {
    return {
      notFound: true,
    };
  }

  // Convert
  allData.header = await headerResponse.json();
  allData.footer = await footerResponse.json();
  allData.marketing = await marketingResponse.json();
  allData.content = await contentResponse.json();
  allData.pages = await pagesResponse.json();
  // Wrap
  const header = {
    header: allData.header,
  };
  const footer = {
    footer: allData.footer,
  };

  // Combine all data
  const data = await {
    ...header,
    ...footer,
    ...allData.marketing,
    ...allData.content,
    ...allData.pages,
  };

  return {
    props: {
      data,
      fallback: false,
    },
  };
}

const BookingIdeas = ({ data }) => {
  return (
    <section className="container take-booking-sec lg:mb-20 lg:mt-20 md:mb-10 md:mt-10">
      <article className="take-booking-content mx-auto lg:maxw-lg">
        <h2
          className="title lg:mb-3 md:mb-2"
          dangerouslySetInnerHTML={{ __html: data.heading }}
        ></h2>
        <p className="desc">{data.desc}</p>
      </article>
      <div className="fx fx--ch booking-ideas mt-5">
        {data.ideas.map((item, index) => {
          return (
            <a className="booking-ideas-item" href={item.pagelink} key={index}>
              <img
                src={item.src}
                alt={item.alt}
                width={64}
                loading="lazy"
                height={64}
              />
            </a>
          );
        })}
      </div>

      <div className="nav-wrap mt-5">
        <a className="btn-secondary md:w-100" href={data.link.src}>
          {data.link.text}
        </a>
      </div>
      <style jsx>
        {`
          .take-booking-sec {
            background-color: ${colors.primitive.grey[600]};
            text-align: center;
            padding: 80px 124px;
            border-radius: 8px;
          }
          .title {
            font-size: ${font.size.lg[2.5]};
            line-height: ${lineHeight[12]};
          }
          .booking-ideas-item {
            cursor: pointer;
            width: 80px;
            height: 80px;
            padding: 8px;
            object-fit: contain;
            border-radius: 16px;
            background-color: ${colors.primitive.white.default};
            box-shadow: 0 1px 2px 0 rgba(48, 58, 71, 0.2),
              0 1px 3px 0 rgba(48, 58, 71, 0.1);
          }
          .booking-ideas-item:hover {
            box-shadow: 0 2px 4px 0 rgba(48, 58, 71, 0.1),
              0 4px 8px 0 rgba(48, 58, 71, 0.14);
          }
          .booking-ideas-item img {
            width: 64px;
          }
          .booking-ideas-item:not(:last-child) {
            margin-right: 24px;
          }
          @media (max-width: ${viewport.tablet.md}) {
            .title {
              font-size: ${font.size.lg[1]};
              line-height: ${lineHeight[10]};
            }
            .booking-ideas {
              flex-wrap: wrap;
            }
            .booking-ideas-item {
              margin-top: 24px;
            }
          }
          @media (max-width: ${viewport.mobile.lg}) {
            .take-booking-sec {
              padding: 20px;
            }
            .booking-ideas-item img {
              margin-top: 4px;
            }
            .booking-ideas-item {
              padding: 4px;
              margin-right: 8px !important;
              margin-top: 16px;
            }
          }
        `}
      </style>
    </section>
  );
};
const FeedbackSection = ({
  data,
  toggleVideo,
  isVideoModalVisible,
  videoId,
}) => {
  return (
    <section className="home-masonry bg-offset lg:pb-10">
      <article className="container lg:maxw-lg lg:text-center md:text-left">
        <h2
          className="text-xl mb-3"
          dangerouslySetInnerHTML={{ __html: data.masonry.title }}
        />
        <p
          className="mx-auto card-desc lg:mb-9 md:mb-5"
          dangerouslySetInnerHTML={{ __html: data.masonry.desc }}
        />
      </article>
      <CarouselFullWidth
        data={data.carouselItems}
        toggleVideo={(id) => {
          toggleVideo(id);
        }}
        isVideoModalVisible={isVideoModalVisible}
        videoId={videoId}
      />
      <div className="testimonial-container" tabIndex="-1"></div>
      <div className="text-center mt-5 md:px-3">
        <a href={data.masonry.navLink} className="btn-secondary md:w-100">
          {data.masonry.navText}
        </a>
      </div>
      <style global jsx>
        {`
          .home-masonry {
            .carousel-cont p {
              font-family: ${font.style.primaryFont};
            }
          }
        `}
      </style>
    </section>
  );
};
export default function Index({ data, nonce }) {
  const { header, footer, seo, content, pages } = data;
  const allBusinessPages = [].concat(
    ...Object.values(pages.featuredList["promote your business"]?.lists ?? [])
  );
  const allCustomersPages = [].concat(
    ...Object.values(pages.featuredList["get more customer"]?.lists ?? [])
  );
  const allBrandsPages = [].concat(
    ...Object.values(pages.featuredList["build your brand"]?.lists ?? [])
  );
  const allBookingPages = [].concat(
    ...Object.values(
      pages.featuredList["personalize online booking"]?.lists ?? []
    )
  );
  const allProductivityPages = [].concat(
    ...Object.values(pages.featuredList["increase productivity"]?.lists ?? [])
  );

  const allPages = [
    ...allBusinessPages,
    ...allCustomersPages,
    ...allBrandsPages,
    ...allBookingPages,
    ...allProductivityPages,
  ];

  const subNavMasonryContainerData = {
    subNavData: content.subNav,
    noSearchResultData: content.searchResult,
    pagesListData: pages,
    allPagesList: allPages,
  };

  const SubNavMasonryContainer = SubNavWithPagesHOC(
    SubNav,
    PagesList,
    EmptyState,
    ResultsContainer,
    subNavMasonryContainerData
  );
  function BlogSection() {
    return (
      <>
        <BlogCardsSection data={content.blog} />
        <div className="text-center md:px-3">
          <a href={content.blog.navLink} className="btn-secondary md:w-100">
            {content.blog.navText}
          </a>
        </div>
      </>
    );
  }
  const toggleVideo = (id) => {
    window.closeModal = () => {
      const testimonialModal = document.getElementById("testimonial-modal");
      testimonialModal.remove();
    };
    const modalPlayer = `
    <div class="testimonial-modal" onclick="closeModal()" id="testimonial-modal">
    <div class="video-content">
      <div class="video-cont is-played">
        <iframe
          id="videoUtube"
          frameBorder="0"
          allowFullScreen="1"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          title="YouTube video player"
          width="960"
          height="539"
          src="https://www.youtube.com/embed/${id}?autoplay=1&amp;controls=1&amp;showinfo=0&amp;modestbranding=1&amp;loop=1&amp;fs=0&amp;cc_load_policy=0&amp;iv_load_policy=3&amp;autohide=0&amp;enablejsapi=1&amp;origin=https%3A%2F%2Fwww.setmore.com&amp;widgetid=2"
          data-gtm-yt-inspected-1_19="true"
        ></iframe>
      </div>
      <div class="text-center">
        <a
          href=${
            process.env.APP_MODE === "live"
              ? `${CONSTANTS.live.universalSignupUrl}/start-now`
              : `${CONSTANTS.staging.universalSignupUrl}/start-now`
          }
          target="_blank"
          class="btn-primary lg:mt-5 md:mt-2.5"
          id="button-hero-cta"
        >
          <span>Get started</span>
        </a>
      </div>
    </div>
    <button id="closeBtn">
      Close
      <svg  width="24" height="24" className="ml-1">
      <use href="/sprite.svg#modal-close"></use>
    </svg>
    </button>
    </div>`;
    document.body.querySelector(".testimonial-container").innerHTML =
      modalPlayer;
    document.body.querySelector(".testimonial-container").focus();
  };

  const handleVideoCarousel = () => {
    const carousel = {
      container: document.querySelector(".carousel"),
      item: Array.prototype.slice.call(
        document.querySelectorAll(".carousel-item")
      ),
    };

    const moveCarousel = () => {
      if (window.innerWidth > 768) {
        carousel.container.style.transform = "translate(-660px)";
      } else {
        carousel.container.style.transform = "translate(-590px)";
      }
    };

    const playFocusedCardVideo = () => {
      carousel.container.children[1]?.children[0]?.children[0]?.children[0].pause();
      carousel.container.children[2]?.children[0]?.children[0]?.children[0].pause();
      carousel.container.appendChild(carousel.container.firstElementChild);
      carousel.container.style.transition = "none";
      if (window.innerWidth > 768) {
        carousel.container.style.transform = "translate(-260px)";
        carousel.container.children[2]?.children[0]?.children[0]?.children[0].play();
      } else {
        carousel.container.style.transform = "translate(-275px)";
        carousel.container.children[1]?.children[0]?.children[0]?.children[0].play();
      }
      setTimeout(function () {
        carousel.container.style.transition = "all 0.8s";
      });
    };
    setInterval(moveCarousel, 8000);
    carousel.container.addEventListener("transitionend", function (e) {
      if (e.target != this) {
        return;
      } else {
        playFocusedCardVideo();
      }
    });
  };

  useEffect(() => {
    handleVideoCarousel();
  }, []);

  return (
    <>
      <Theme
        header={header}
        footer={footer}
        seo={seo}
        nonce={nonce}
        className="guides-home"
      >
        <AppStorage pageName={`Guides Root Page`} />
        <HeroWithLayeredImages
          data={content.hero}
          hasTpwidgetMicro={true}
          sectionClassName="lg:mb-0 guides-hero"
        />
        <SubNavMasonryContainer />
        <HubCardsWithScroll data={content.hubPagesSection} />
        <BookingIdeas data={content.bookingideas} />
        <section className="wrapper-section lg:pt-20 md:pt-10">
          <FeedbackSection
            data={content.blogCarousel}
            toggleVideo={(id) => {
              toggleVideo(id);
            }}
          />
          <BlogSection />
          <DoubleCTA
            data={content.doubleCTA}
            className="lg:mt-20 lg:pb-20 md:pb-10 md:mt-10"
          />
        </section>
        <SignUpCTA data={content.signUpCTA} isDesc />
      </Theme>
      <style global jsx>
        {`
          .guides-hero {
            padding-bottom: 40px !important;
          }
          .guides-hero .hero--content {
            margin-right: 48px !important;
          }
          .wrapper-section {
            background-color: ${colors.primitive.grey[600]};
          }
          .wrapper-section .bg-light {
            background-color: transparent !important;
          }
          @media (max-width: ${viewport.tablet.sm}) {
            .guides-hero {
              padding-bottom: 0px !important;
            }
          }
        `}
      </style>
    </>
  );
}
